import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

// Matches the common stale-bundle errors emitted by Next.js / webpack route loader
// when the client requests a chunk that no longer exists after a deploy.
// Examples:
//   "Loading chunk 42 failed"
//   "Loading CSS chunk 7 failed"
//   "Failed to load script: /_next/static/chunks/pages/bid_requests-abc.js"
//   "ChunkLoadError: ..."
const CHUNK_LOAD_ERROR_PATTERN = /Loading (?:CSS )?chunk \d+ failed|Failed to load script|ChunkLoadError/i;

const isChunkLoadError = (input: unknown): boolean => {
  if (!input) return false;
  if (input instanceof Error) {
    if (input.name === 'ChunkLoadError') return true;
    return CHUNK_LOAD_ERROR_PATTERN.test(input.message);
  }
  if (typeof input === 'string') return CHUNK_LOAD_ERROR_PATTERN.test(input);
  return false;
};

/**
 * Registers global handlers that hard-reload the page when a Next.js / webpack
 * chunk fails to load. This is the standard remedy for the stale-chunk problem
 * that occurs after a deploy invalidates the bundle manifest the running tab
 * was holding onto.
 *
 * A `useRef` guard ensures we only trigger one reload per page lifetime, so
 * users never get stuck in a reload loop if the bundle is truly broken.
 */
export const useChunkReload = (): void => {
  const router = useRouter();
  const hasReloadedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const reload = (targetPath?: string) => {
      if (hasReloadedRef.current) return;
      hasReloadedRef.current = true;
      const target = targetPath ?? router.asPath ?? window.location.pathname;
      window.location.assign(target);
    };

    const handleRouteChangeError = (err: Error, url: string) => {
      if (!isChunkLoadError(err)) return;
      reload(url);
    };

    const handleWindowError = (event: ErrorEvent) => {
      if (!isChunkLoadError(event.error) && !isChunkLoadError(event.message)) return;
      reload();
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (!isChunkLoadError(event.reason)) return;
      reload();
    };

    router.events.on('routeChangeError', handleRouteChangeError);
    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      router.events.off('routeChangeError', handleRouteChangeError);
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [router]);
};
